import React, { useEffect, useState } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dropdown from "../Input/Dropdown";
import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
} from "@mui/material";

interface Option {
    value: string | number;
    label: string;
    count?: number;
    color?: string;
    bgColor?: string;
}

interface FilterMultiSelectProps {
    options: Option[];
    value: any[];
    onChange: (values: any[]) => void;
    maxWidth?: string | number;
    multiSelect?: boolean;
    showCounts?: boolean;
    hideAll?: boolean;
    heading?: string;
    defaultFilter?: string | number;
}

const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
    options,
    value = [],
    onChange,
    maxWidth = "500px",
    multiSelect = true,
    showCounts = true,
    hideAll = false,
    heading,
    defaultFilter,
}) => {
    const list = options.map((opt) => opt.value.toString());

    const filterCount = options.reduce((acc, opt) => {
        acc[opt.value.toString()] = opt.count || 0;
        return acc;
    }, {} as Record<string, number>);

    const active = value.map((v) => v.toString());

    const handleChange = (newValues: string[]) => {
        const originalTypeValues = newValues.map((val) => {
            const originalOption = options.find(
                (opt) => opt.value.toString() === val
            );
            return originalOption ? originalOption.value : val;
        });
        onChange(originalTypeValues);
    };

    useEffect(() => {
        if (value.length === 0 && options.length > 0) {
            const firstOption = options[0].value;
            onChange([firstOption]);
        }
    }, [options, value.length, onChange]);

    const setActiveFilter = (newActive: string[]) => {
        const originalTypeValues = newActive.map((val) => {
            const originalOption = options.find(
                (opt) => opt.value.toString() === val
            );
            return originalOption ? originalOption.value : val;
        });

        onChange(originalTypeValues);
    };

    const totalCount = () => {
        let temp = 0;
        Object.keys(filterCount).forEach((filter) => {
            if (filterCount[filter]) {
                temp += filterCount[filter];
            }
        });
        return temp;
    };

    const styles = options.map((opt) => ({
        color: opt.color || "#ffffff",
        background: "#ffffff",
    }));

    const stylesActive = options.map((opt) => ({
        color: opt.color || "#ffffff",
        background: opt.bgColor || "#ffffff",
    }));

    const allStyle = { color: "#000000", background: "#ffffff" };
    const allStyleActive = { color: "#ffffff", background: "#1976d2" };

    return (
        <Box
            sx={{
                maxWidth,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                    height: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#bdbdbd",
                    borderRadius: "4px",
                },
                marginLeft: "auto",
            }}
            key={`filter-${heading}`}
        >
            <div className="filter">
                {heading ? <div className="heading">{heading}</div> : null}
                <ButtonGroup
                    variant="contained"
                    color="primary"
                    aria-label="filter options"
                    className="filter-container-btn-grp H100 filterNormal"
                    style={{
                        width: "100%",
                        overflowX: "auto",
                        borderRadius: "4px",
                        backgroundColor: "#ffffff",
                    }}
                    sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        "& .MuiButton-root": {
                            // borderRadius: "4px",
                            // margin: "0 2px",
                            textTransform: "none",
                            minWidth: "auto",
                            fontWeight: 400,
                            borderColor: "#e0e0e0",
                            padding: "6px 12px",
                            fontSize: "0.875rem",
                            boxShadow: "none",
                        },
                    }}
                >
                    {!hideAll
                        ? !multiSelect && (
                              <Button
                                  className="tab"
                                  key={"filter"}
                                  onClick={() => {
                                      //   setActive(list);
                                      setActiveFilter(list);
                                  }}
                                  style={
                                      active.length > 1
                                          ? allStyleActive
                                          : allStyle
                                  }
                              >
                                  All ({showCounts && totalCount()})
                              </Button>
                          )
                        : null}
                    {options.map((option, ind) => (
                        <Button
                            className="tab p10I"
                            key={`filter-${option.value}`}
                            onClick={() => {
                                let temp = [...active];
                                const optValue = option.value.toString();

                                if (active.includes(optValue)) {
                                    temp = temp.filter(
                                        (val) => val !== optValue
                                    );
                                } else {
                                    temp = [...temp, optValue];
                                }

                                // setActive(multiSelect ? temp : [optValue]);
                                setActiveFilter(
                                    multiSelect ? temp : [optValue]
                                );
                            }}
                            style={
                                multiSelect
                                    ? active.includes(option.value.toString())
                                        ? stylesActive[ind]
                                        : styles[ind]
                                    : active[0] === option.value.toString() &&
                                      active.length === 1
                                    ? stylesActive[ind]
                                    : styles[ind]
                            }
                            sx={{
                                whiteSpace: "nowrap",
                                "&:hover": {
                                    opacity: 0.9,
                                    boxShadow: "none",
                                },
                                boxShadow: "none !important",
                                paddingLeft: !(multiSelect &&
                                active.includes(option.value.toString())) ? "20.4px !important" : "10px !important",
                                paddingRight: !(multiSelect &&
                                active.includes(option.value.toString())) ? "20.4px !important" : "10px !important",
                            }}
                        >
                            {option.label}
                            {showCounts && (
                                <span style={{ marginLeft: "4px" }}>
                                    ({option.count || 0})
                                </span>
                            )}
                            {multiSelect &&
                                active.includes(option.value.toString()) && (
                                    <CancelIcon
                                        style={{
                                            fontSize: "1.2em",
                                            marginBottom: "3px",
                                            marginLeft: "4px",
                                        }}
                                    />
                                )}
                        </Button>
                    ))}
                </ButtonGroup>

                <FormControl
                    size="small"
                    fullWidth
                    className="filterSmall"
                    sx={{ marginTop: 3 }}
                >
                    <Autocomplete
                        multiple
                        options={options || []}
                        disableCloseOnSelect
                        limitTags={2}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <span
                                    {...getTagProps({ index })}
                                    style={{
                                        fontSize: "14px",
                                        margin: 0,
                                    }}
                                >
                                    {`${option.label}${
                                        index === value.length - 1 ? "" : ", "
                                    }`}
                                </span>
                            ))
                        }
                        getOptionLabel={(option) => option.label}
                        value={
                            options?.filter((option) =>
                                active.includes(option.value.toString())
                            ) || []
                        }
                        onChange={(event, newValue) => {
                            const newValues = newValue.map((item) =>
                                item.value.toString()
                            );

                            // setActive(
                            //     multiSelect
                            //         ? newValues
                            //         : [newValues[newValues.length - 1] || ""]
                            // );
                            setActiveFilter(
                                multiSelect
                                    ? newValues
                                    : [newValues[newValues.length - 1] || ""]
                            );
                        }}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox checked={selected} />
                                {option.label}
                                {showCounts && ` (${option.count || 0})`}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={heading}
                                variant="outlined"
                                // size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        bgcolor: "white",
                                        height: "47px",
                                    },
                                }}
                            />
                        )}
                    />
                </FormControl>
            </div>
        </Box>
    );
};

export default FilterMultiSelect;
